import * as React from "react"
import { Calendar, ExternalLink, ImageIcon } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./card"
import { ButtonWidget } from "./button-widget"
import { cn } from "../../lib/utils"

export interface ContentEntry {
  title: string
  description: string
  date: string
  link: string
  linkText?: string
  image?: string
}

interface GridViewProps {
  entries: ContentEntry[]
  columns?: 1 | 2 | 3 | 4
  className?: string
  showImages?: boolean
}

const GridView = React.forwardRef<HTMLDivElement, GridViewProps>(
  ({ entries, columns = 3, className, showImages = true, ...props }, ref) => {
    const gridCols = {
      1: "dsw-grid-cols-1",
      2: "dsw-grid-cols-1 md:dsw-grid-cols-2",
      3: "dsw-grid-cols-1 md:dsw-grid-cols-2 lg:dsw-grid-cols-3",
      4: "dsw-grid-cols-1 md:dsw-grid-cols-2 lg:dsw-grid-cols-3 xl:dsw-grid-cols-4"
    }

    return (
      <div
        ref={ref}
        className={cn(
          "dsw-grid dsw-gap-4",
          gridCols[columns],
          className
        )}
        {...props}
      >
        {entries.map((entry, index) => (
          <Card key={index} className="dsw-h-full dsw-flex dsw-flex-col">
            {showImages && (
              <div className="dsw-aspect-video dsw-bg-muted dsw-flex dsw-items-center dsw-justify-center dsw-rounded-t-lg">
                {entry.image ? (
                  <img 
                    src={entry.image} 
                    alt={entry.title}
                    className="dsw-w-full dsw-h-full dsw-object-cover dsw-rounded-t-lg"
                  />
                ) : (
                  <ImageIcon className="dsw-h-12 dsw-w-12 dsw-text-muted-foreground" />
                )}
              </div>
            )}
            <CardHeader className="dsw-flex-1">
              <CardTitle className="dsw-line-clamp-2">{entry.title}</CardTitle>
              <CardDescription className="dsw-line-clamp-3">
                {entry.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="dsw-pt-0">
              <div className="dsw-flex dsw-items-center dsw-justify-between dsw-gap-2">
                <span className="dsw-flex dsw-items-center dsw-gap-1 dsw-text-xs dsw-text-muted-foreground">
                  <Calendar className="dsw-h-3 dsw-w-3" />
                  {entry.date}
                </span>
                <ButtonWidget variant="outline" size="sm" asChild>
                  <a href={entry.link} target="_blank" rel="noopener noreferrer" className="dsw-flex dsw-items-center">
                    <ExternalLink className="dsw-h-3 dsw-w-3 dsw-mr-1" />
                    {entry.linkText || "View"}
                  </a>
                </ButtonWidget>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }
)
GridView.displayName = "GridView"

export { GridView } 